import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { addBreadcrumb } from '@sentry/react';
import { ErrorResponse } from 'oidc-client-ts';

import { useAuth } from '@xemplo/auth-provider';
import { CommonNavigate } from '@xemplo/common-routes';
import { AuthBroadcastMessage, AuthBroadcastType } from '@xemplo/cross-app-events';
import { safeCallAsync } from '@xemplo/safe-call';

import {
  getActiveSub,
  getAuthChannel,
  getLogoutAt,
} from '../helpers/auth-broadcast.helper';

/**
 * Session auth time (epoch seconds) from the id_token — read here, not from
 * `user.profile` (oidc-client-ts strips auth_time); stable across renews unlike iat.
 */
function getAuthTimeSeconds(idToken: string | undefined): number | null {
  const payload = idToken?.split('.')[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded));
    return typeof claims.auth_time === 'number' ? claims.auth_time : null;
  } catch {
    return null;
  }
}

/**
 * Keeps this stack's session in sync with cross-app auth changes (expedo-fe ↔ hr-fe,
 * same-origin apps this stack shares `xemplo-auth` with; other Xemplo apps on a
 * different origin, e.g. Hire; and across tabs of the same stack). Handles four
 * crossings:
 *
 * - **Logout** — a live `xemplo-auth` broadcast tears down open tabs; the durable
 *   `logoutAt` marker tears down a tab that loads later in the same tab.
 * - **User switch** — a live `login` broadcast, or a mismatch between the cached
 *   user's `sub` and the durable `activeSub` marker on load, means a *different*
 *   identity is now active (logout → log in as someone else → cross back). A
 *   logout-only signal misses this because the subsequent login clears `logoutAt`.
 * - **Cross-origin logout** — `xemplo-auth` is a `BroadcastChannel`, scoped to one
 *   origin, so it never reaches an app like Hire that shares the IdP but not the
 *   origin. `monitorSession` (set on `EIMS_CLIENT_CONFIG`) polls the IdP's
 *   `check_session_iframe` instead, and oidc-client-ts raises `userSignedOut` when
 *   that reports no session — from a logout in any app on the same IdP.
 *
 * Re-establishment routes to login, where the silent sign-in adopts the new identity
 * from the shared SSO session without an interactive prompt (falling back to the full
 * redirect only when there is no IdP session — the outcome for a genuine cross-app
 * logout). Mount once app-wide (see `Root`).
 */
export function useCrossAppAuthListener(): void {
  const { user, removeUser, events, signinSilent } = useAuth();
  const navigate = useNavigate();
  const localSub = user?.profile?.sub;
  // auth_time is stable across silent renewals, so deriving it here (rather than
  // depending on the whole `user`, whose reference changes every renewal) keeps the
  // durable effect from re-running — and reading localStorage — on every refresh.
  const authTime = useMemo(() => getAuthTimeSeconds(user?.id_token), [user?.id_token]);

  // Drop the local OIDC user and route to login. The login page's silent sign-in
  // then re-adopts whichever identity the shared SSO session now holds.
  const reestablishViaLogin = useCallback(async () => {
    await safeCallAsync(removeUser());
    navigate(CommonNavigate.Login, { replace: true });
  }, [removeUser, navigate]);

  const handleMessage = useCallback(
    (event: MessageEvent<AuthBroadcastMessage>) => {
      const message = event.data;
      if (message?.type === AuthBroadcastType.Logout) return void reestablishViaLogin();

      // Only react when this tab actually holds a *different* identity. An
      // unauthenticated tab (no localSub) has nothing stale to fix, so it ignores a
      // login broadcast — matching the durable effect, which also requires localSub.
      const switchedUser =
        message?.type === AuthBroadcastType.Login &&
        !!localSub &&
        !!message.sub &&
        message.sub !== localSub;
      if (switchedUser) void reestablishViaLogin();
    },
    [reestablishViaLogin, localSub],
  );

  useEffect(() => {
    const channel = getAuthChannel();
    if (!channel) return;

    channel.addEventListener('message', handleMessage);
    return () => channel.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  /**
   * oidc-client-ts raises `userSignedOut` both when the OP cleanly reports no
   * session, and on an unrelated false positive from `check_session_iframe` —
   * most commonly its cross-origin cookie read being blocked (third-party
   * cookie blocking, or an app origin that isn't same-site with the IdP, e.g.
   * local dev). `querySessionStatus()` goes through the same iframe, so it
   * can't tell the two apart either.
   *
   * `signinSilent()` can: with `offline_access` in scope (ADR 0015), a stored
   * user renews via a plain `POST /connect/token` using the refresh token, no
   * iframe involved. A logout that actually revoked the session also revokes
   * the refresh token, so the OP rejects that request with an `ErrorResponse`
   * (e.g. `invalid_grant`) — a genuine signal, independent of the iframe. Any
   * other failure (network blip, IdP unreachable) is inconclusive and is
   * reported rather than acted on, so it can't sign the user out by mistake.
   */
  const handleIdpSignedOut = useCallback(async () => {
    const [, error] = await safeCallAsync(signinSilent());
    if (!error) return;

    if (error instanceof ErrorResponse) {
      void reestablishViaLogin();
      return;
    }

    addBreadcrumb({
      category: 'auth',
      message:
        'useCrossAppAuthListener.handleIdpSignedOut: inconclusive signinSilent failure',
      level: 'warning',
      data: { error: error instanceof Error ? error.message : String(error) },
    });
  }, [signinSilent, reestablishViaLogin]);

  useEffect(() => {
    events.addUserSignedOut(handleIdpSignedOut);
    return () => events.removeUserSignedOut(handleIdpSignedOut);
  }, [events, handleIdpSignedOut]);

  // Durable backstop for a tab that loads after the crossing (navigate away → switch
  // or log out elsewhere → return). Identity mismatch takes precedence: it catches a
  // user switch that the logout marker can't, because login clears that marker.
  useEffect(() => {
    if (!localSub) return;

    const activeSub = getActiveSub();
    if (activeSub && activeSub !== localSub) {
      void reestablishViaLogin();
      return;
    }

    // Falls through when there is no active-identity marker yet (e.g. the other stack
    // hasn't published one): logout propagation degrades to the auth_time comparison.
    const logoutAt = getLogoutAt();
    if (!logoutAt) return;

    if (authTime != null && authTime * 1000 < logoutAt) {
      void reestablishViaLogin();
    }
  }, [localSub, authTime, reestablishViaLogin]);
}
